import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  FormGroup,
} from "reactstrap";
import { getCashLedgers, exportCashLedgerToExcel } from "../../components/Api/GmsCashLedger";
import { getBankAccounts } from "../../components/Api/GmsBankAccount";
import { getCostHeads } from "../../components/Api/GmsCostHead";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getSuppliers } from "../../components/Api/GmsSupplier";
import { getOtherPayees } from "../../components/Api/GmsOtherPayee";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission,
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForDisplay } from "../../helpers/dateHelper";
import { Download } from "lucide-react";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
    overflowY: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const emptyFilter = {
  FromDate: null,
  ToDate: null,
  TransactorType: null,
  TransactorId: null,
};

export default function PettyCashReportPage() {
  const [cashLedgers, setCashLedgers] = useState([]);
  const [filter, setFilter] = useState(emptyFilter);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [exporting, setExporting] = useState(false);
  
  // Transactor data for name mapping
  const [bankAccounts, setBankAccounts] = useState([]);
  const [costHeads, setCostHeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [otherPayees, setOtherPayees] = useState([]);
  
  // Permission states
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsPettyCashReport');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsPettyCashReport');
  
  // Transactor type options (Cost Head removed - it's now a separate field)
  const transactorTypeOptions = [
    { value: 'ba', label: 'Banks' },
    { value: 'em', label: 'Employee' },
    { value: 'su', label: 'Supplier' },
    { value: 'ot', label: 'Others' },
  ];
  
  useEffect(() => {
    if (hasPermission) {
      loadTransactorData();
      loadCashLedgers();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, page, pageSize]);
  
  const loadTransactorData = async () => {
    try {
      // Load all transactor data for name mapping
      const [bankAccountsData, costHeadsData, employeesData, suppliersData, otherPayeesData] = await Promise.all([
        getBankAccounts({}), // Load all bank accounts
        getCostHeads({}),
        getEmployees({}),
        getSuppliers({}),
        getOtherPayees({}),
      ]);
      
      // Extract arrays from response objects
      const bankAccountsArray = Array.isArray(bankAccountsData?.bankAccounts) ? bankAccountsData.bankAccounts : (Array.isArray(bankAccountsData) ? bankAccountsData : []);
      setBankAccounts(bankAccountsArray);
      setCostHeads(Array.isArray(costHeadsData) ? costHeadsData : []);
      setEmployees(Array.isArray(employeesData) ? employeesData : []);
      setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
      setOtherPayees(Array.isArray(otherPayeesData) ? otherPayeesData : []);
    } catch (error) {
      console.error("Error loading transactor data:", error);
      // Don't show error to user, just log it
    }
  };
  
  const loadCashLedgers = async (filterData = filter) => {
    try {
      setLoading(true);
      const filterPayload = {
        ...filterData,
        FilterType: null, // No filter - get all entries (both debit and credit)
        PageNumber: page,
        PageSize: pageSize,
      };
      
      const result = await getCashLedgers(filterPayload);
      
      setCashLedgers(result.cashLedgers || []);
      setTotalCount(result.totalCount || 0);
      setTotalPages(result.totalPages || 0);
    } catch (error) {
      console.error("Error loading cash ledger entries:", error);
      Swal.fire("Error", "Failed to load cash ledger entries", "error");
    } finally {
      setLoading(false);
    }
  };
  
  const handleFilterChange = (field, value) => {
    setFilter(prev => ({ ...prev, [field]: value }));
  };
  
  const handleApplyFilter = () => {
    setPage(1);
    loadCashLedgers(filter);
  };
  
  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadCashLedgers(emptyFilter);
  };
  
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      
      // Prepare filter for export (no pagination)
      const exportFilter = {
        FromDate: filter.FromDate,
        ToDate: filter.ToDate,
        TransactorType: filter.TransactorType,
        TransactorId: filter.TransactorId,
        FilterType: null, // Get all entries (both debit and credit)
      };
      
      await exportCashLedgerToExcel(exportFilter);
      Swal.fire("Success", "Petty cash report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting petty cash report:", error);
      Swal.fire("Error", "Failed to export petty cash report", "error");
    } finally {
      setExporting(false);
    }
  };
  
  const getTransactorTypeLabel = (type) => {
    if (!type) return '-';
    const option = transactorTypeOptions.find(opt => opt.value === type);
    return option ? option.label : type;
  };
  
  const getTransactorName = (transactorType, transactorId) => {
    if (!transactorType || !transactorId) return '-';
    
    try {
      switch (transactorType) {
        case 'ba':
          // For banks, transactorId is bankAccountId
          const bankAccount = bankAccounts.find(acc => 
            (acc.bankAccountId || acc.BankAccountId || acc.bankAccount_Id || acc.BankAccount_Id) == transactorId
          );
          if (bankAccount) {
            const accountHolderName = bankAccount.accountHolderName || bankAccount.AccountHolderName || '';
            const accountNumber = bankAccount.accountNumber || bankAccount.AccountNumber || '';
            return accountHolderName && accountNumber 
              ? `${accountHolderName} - ${accountNumber}`
              : accountHolderName || accountNumber || '-';
          }
          return '-';
          
        case 'ch':
          const costHead = costHeads.find(ch => 
            (ch.costHead_Id || ch.CostHead_Id) == transactorId
          );
          return costHead ? (costHead.costHead_Name || costHead.CostHead_Name || '-') : '-';
          
        case 'em':
          const employee = employees.find(emp => 
            (emp.employee_Id || emp.Employee_Id || emp.employeeId || emp.EmployeeId) == transactorId
          );
          if (employee) {
            const name = employee.employee_NameEn || employee.Employee_NameEn || employee.employeeNameEn || employee.EmployeeNameEn || '';
            return name || '-';
          }
          return '-';
          
        case 'su':
          const supplier = suppliers.find(sup => 
            (sup.supplier_Id || sup.Supplier_Id || sup.supplierId || sup.SupplierId) == transactorId
          );
          return supplier ? (supplier.supplier_NameEn || supplier.Supplier_NameEn || supplier.supplierNameEn || supplier.SupplierNameEn || supplier.supplier_Name || supplier.Supplier_Name || '-') : '-';
          
        case 'ot':
          const otherPayee = otherPayees.find(op => 
            (op.otherPayee_Id || op.OtherPayee_Id || op.otherPayeeId || op.OtherPayeeId) == transactorId
          );
          return otherPayee ? (otherPayee.otherPayee_Name || otherPayee.OtherPayee_Name || otherPayee.otherPayeeName || otherPayee.OtherPayeeName || '-') : '-';
          
        default:
          return '-';
      }
    } catch (error) {
      console.error("Error getting transactor name:", error);
      return '-';
    }
  };
  
  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };
  
  const getCostHeadName = (costHeadId) => {
    if (!costHeadId) return '-';
    const costHead = costHeads.find(ch => 
      (ch.costHead_Id || ch.CostHead_Id) == costHeadId
    );
    return costHead ? (costHead.costHead_Name || costHead.CostHead_Name || '-') : '-';
  };
  
  // Calculate totals
  const totals = useMemo(() => {
    const totalDebit = cashLedgers.reduce((sum, entry) => {
      const debit = entry.debit || entry.Debit || 0;
      return sum + parseFloat(debit);
    }, 0);
    
    const totalCredit = cashLedgers.reduce((sum, entry) => {
      const credit = entry.credit || entry.Credit || 0;
      return sum + parseFloat(credit);
    }, 0);
    
    return {
      totalDebit,
      totalCredit,
      balance: totalDebit - totalCredit
    };
  }, [cashLedgers]);
  
  // Pagination component
  const Pagination = () => {
    if (totalPages <= 1) return null;
    
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    
    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
        </div>
        <div className="d-flex gap-1">
          <Button
            color="primary"
            size="sm"
            disabled={page === 1}
            onClick={() => handlePageChange(page - 1)}
          >
            Previous
          </Button>
          {pages.map(pageNum => (
            <Button
              key={pageNum}
              color={pageNum === page ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => handlePageChange(pageNum)}
            >
              {pageNum}
            </Button>
          ))}
          <Button
            color="primary"
            size="sm"
            disabled={page === totalPages}
            onClick={() => handlePageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };
  
  if (!hasPermission) {
    return null;
  }
  
  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Petty Cash Report" breadcrumbItem="Petty Cash Report" />
        
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0">Petty Cash Report</h4>
                  <div className="d-flex gap-2">
                    <Button
                      color="success"
                      onClick={handleExportToExcel}
                      disabled={exporting || loading || cashLedgers.length === 0}
                    >
                      <Download className="me-1" size={16} />
                      {exporting ? "Exporting..." : "Export to Excel"}
                    </Button>
                    <Button
                      color="info"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      {showFilters ? "Hide Filters" : "Show Filters"}
                    </Button>
                  </div>
                </div>
                
                {/* Filters */}
                {showFilters && (
                  <Card className="mb-4">
                    <CardBody>
                      <Row>
                        <Col md={3}>
                          <FormGroup>
                            <Label>From Date</Label>
                            <DatePicker
                              selected={filter.FromDate}
                              onChange={(date) => handleFilterChange("FromDate", date)}
                              dateFormat="dd/MM/yyyy"
                              className="form-control"
                              isClearable
                              placeholderText="Select from date"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label>To Date</Label>
                            <DatePicker
                              selected={filter.ToDate}
                              onChange={(date) => handleFilterChange("ToDate", date)}
                              dateFormat="dd/MM/yyyy"
                              className="form-control"
                              isClearable
                              placeholderText="Select to date"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Transactor Type</Label>
                            <Select
                              value={transactorTypeOptions.find(opt => opt.value === filter.TransactorType) || null}
                              onChange={(option) => handleFilterChange("TransactorType", option?.value || null)}
                              options={transactorTypeOptions}
                              isClearable
                              placeholder="Select transactor type"
                              styles={selectStyles}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <div className="d-flex gap-2 align-items-end h-100">
                            <Button color="primary" onClick={handleApplyFilter}>
                              Apply
                            </Button>
                            <Button color="warning" onClick={handleClearFilter}>
                              Clear
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}
                
                {/* Table */}
                {loading ? (
                  <div className="text-center py-5">
                    <ClipLoader size={50} color="#556ee6" />
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table bordered>
                        <thead className="table-light">
                          <tr>
                            <th>Sr.No</th>
                            <th>Date</th>
                            <th>Debit</th>
                            <th>Credit</th>
                            <th>Transactor Type</th>
                            <th>Transactor</th>
                            <th>Cost Head</th>
                            <th>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cashLedgers.length === 0 ? (
                            <tr>
                              <td colSpan="8" className="text-center">
                                No transactions found
                              </td>
                            </tr>
                          ) : (
                            cashLedgers.map((entry, index) => {
                              // Handle both camelCase and PascalCase property names
                              const cashLedgerId = entry.cashLedgerID || entry.CashLedgerID;
                              const date = entry.date || entry.Date;
                              const debit = entry.debit || entry.Debit || 0;
                              const credit = entry.credit || entry.Credit || 0;
                              const transactorType = entry.transactorType || entry.TransactorType;
                              const transactorId = entry.transactorId || entry.TransactorId;
                              const costHeadId = entry.costHeadId || entry.CostHeadId;
                              const notes = entry.notes || entry.Notes;
                              
                              return (
                                <tr key={cashLedgerId}>
                                  <td>{(page - 1) * pageSize + index + 1}</td>
                                  <td>{formatDateForDisplay(date, 'date')}</td>
                                  <td>{formatCurrency(debit)}</td>
                                  <td>{formatCurrency(credit)}</td>
                                  <td>{getTransactorTypeLabel(transactorType)}</td>
                                  <td>{getTransactorName(transactorType, transactorId)}</td>
                                  <td>{getCostHeadName(costHeadId)}</td>
                                  <td>{notes || '-'}</td>
                                </tr>
                              );
                            })
                          )}
                          {/* Totals Row */}
                          {cashLedgers.length > 0 && (
                            <tr className="table-info fw-bold">
                              <td colSpan="2" className="text-end">Total:</td>
                              <td>{formatCurrency(totals.totalDebit)}</td>
                              <td>{formatCurrency(totals.totalCredit)}</td>
                              <td colSpan="3" className="text-end">Balance:</td>
                              <td className={totals.balance >= 0 ? 'text-success' : 'text-danger'}>
                                {formatCurrency(totals.balance)}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                    
                    <Pagination />
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

